// Résolution des URLs des médias utilisateurs (rail d'historique, cartes du
// flux, /content, /rabbits…) avec cache mémoire path+rendition → URL : les
// listes sont temps réel (onSnapshot), on ne re-résout jamais deux fois la
// même rendition du même chemin.
//
// US R2-05 (migration médias → Cloudflare R2, TERMINÉE en US R2-11) : les
// chemins du périmètre migré (generations/, characters/, datasetDrafts/,
// staging/characters/, project_thumbnails/) sont résolus par la callable
// signMediaUrls — le « videur » serveur (US R2-04) qui reproduit les
// contrôles owner/admin de storage.rules et renvoie des URLs Worker signées
// HMAC, avec renditions à la volée (width 256 vignettes, 768 galerie,
// absente = original). Tout le reste (golden source prompts/, templates/…)
// reste sur les token-URLs Firebase via getFileUrl.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getFileUrl,
  requestSignedMediaUrls,
} from '../../../../services/storage.service';
import { MEDIA_THUMB_WIDTH } from '../../../../constants/mediaRenditions';

// Périmètre migré R2 — miroir de OWNER_READ_PREFIXES de
// functions/signMediaUrls.js (⚠️ garder synchronisé : la callable refuse
// uniformément tout path hors périmètre).
const R2_PATH_PREFIXES = [
  'generations/',
  'characters/',
  'datasetDrafts/',
  'staging/characters/',
  'project_thumbnails/',
];

export const isR2MediaPath = (path: string): boolean =>
  R2_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

// Rendition demandée : width en px, null = original pleine résolution.
export type MediaWidth = number | null;

// Une requête de résolution : un path seul (original) ou { path, width }.
export type StorageUrlRequest =
  | string
  | { path?: string | null; width?: number }
  | null
  | undefined;

// Clé de cache path+rendition : une média = une URL PAR RENDITION (le Worker
// cache chaque transformation en edge — le principe #78 « un média = une URL =
// un cache Chrome » s'applique au niveau de la rendition).
export const urlCacheKey = (path: string, width: MediaWidth = null): string =>
  `${width ?? 0}|${path}`;

const widthOfKey = (key: string): MediaWidth => {
  const width = Number(key.slice(0, key.indexOf('|')));
  return width === 0 ? null : width;
};
const pathOfKey = (key: string): string => key.slice(key.indexOf('|') + 1);

// Cache module : survit aux snapshots et aux hide/show du panneau pendant
// toute la session. BORNÉ (issue #82) : sans éviction, la Map grandissait
// d'une entrée par chemin Storage affiché, jamais purgée.
const MAX_URL_CACHE_ENTRIES = 500;
const urlCache = new Map<string, string>();
const pendingKeys = new Set<string>();

// Chemins actuellement demandés par les consommateurs MONTÉS du hook,
// indexés par clé de consommateur : une entrée encore affichée ne doit
// JAMAIS être évincée. Sans cette garde, dès que les vignettes visibles
// dépassent la borne (fenêtre /content > 500 médias), chaque résolution
// évince une vignette affichée qui est re-résolue au bump suivant, ce qui
// en évince une autre… — boucle infinie de clignotement de la liste.
const demandedKeys = new Map<object, Set<string>>();

const isDemanded = (key: string): boolean => {
  for (const keys of demandedKeys.values()) {
    if (keys.has(key)) return true;
  }
  return false;
};

// EXPIRATION DES URLS SIGNÉES (correctif post-revue US R2-05) : contrairement
// aux token-URLs Firebase, les URLs Worker ont un TTL (7 jours). Une entrée
// expirée (ou proche de l'être — marge 1 h pour les longues sessions
// d'onglet ouvert) n'est JAMAIS servie depuis le cache : elle est purgée et
// re-signée via la callable. Jamais d'image cassée silencieuse.
const WORKER_URL_EXPIRY_MARGIN_MS = 60 * 60 * 1000;

const workerUrlExpiryMs = (url: string): number | null => {
  const match = /[?&]exp=(\d+)/.exec(url);
  return match ? Number(match[1]) * 1000 : null;
};

export const isWorkerSignedUrl = (url: string): boolean =>
  workerUrlExpiryMs(url) !== null;

// URL dénormalisée expirée (ou proche de l'expiration) — utilisé par les
// consommateurs d'URLs stockées (favoris) pour préférer une re-signature.
export const isStorageUrlExpired = (url: string): boolean => isUrlExpired(url);

const isUrlExpired = (url: string): boolean => {
  const expiryMs = workerUrlExpiryMs(url);
  return expiryMs !== null && expiryMs - Date.now() < WORKER_URL_EXPIRY_MARGIN_MS;
};

// Lecture interne : une entrée expirée est supprimée et lue comme absente
// (l'effet du hook la re-résoudra au bump suivant).
const getValidCachedUrl = (key: string): string | undefined => {
  const url = urlCache.get(key);
  if (url && isUrlExpired(url)) {
    urlCache.delete(key);
    return undefined;
  }
  return url;
};

// Éviction FIFO : la Map itère dans l'ordre d'insertion — on purge les
// entrées les plus anciennes au-delà de la borne, EN SAUTANT celles encore
// demandées par un consommateur monté. La borne est donc souple : si tout
// le cache est affiché, on dépasse plutôt que de clignoter. Un chemin
// évincé puis à nouveau demandé est re-résolu automatiquement (l'effet
// ci-dessous se relance à chaque bump de version, voir notifyListeners).
const setCachedUrl = (key: string, url: string) => {
  urlCache.set(key, url);
  let excess = urlCache.size - MAX_URL_CACHE_ENTRIES;
  if (excess <= 0) return;
  for (const oldest of urlCache.keys()) {
    if (excess <= 0) break;
    if (isDemanded(oldest)) continue;
    urlCache.delete(oldest);
    excess -= 1;
  }
};

// Consommateurs montés du hook : chaque LOT résolu (quelle que soit
// l'instance qui a lancé la promesse) re-rend TOUS les consommateurs en un
// broadcast — sinon un effet nettoyé entre-temps (remount StrictMode,
// changement de pathsKey pendant la résolution) laissait les skeletons figés.
const listeners = new Set<() => void>();
const notifyListeners = () => listeners.forEach((listener) => listener());

// URL CANONIQUE (issue #78) : un média = UNE URL d'original dans toute
// l'app, un seul cache Chrome, un seul download facturé. Le cache est amorcé
// avec l'URL signée renvoyée par le serveur à la complétion d'un run
// (run.videoUrl — URL Worker signée) pour que flux, rail,
// historique et /content partagent la même identité URL. Sans effet si le
// chemin est déjà connu (jamais de remplacement : changer l'URL d'un média
// affiché relancerait son téléchargement).
export const seedStorageUrl = (path: string, url: string) => {
  const key = urlCacheKey(path);
  if (getValidCachedUrl(key) || isUrlExpired(url)) return;
  setCachedUrl(key, url);
  notifyListeners();
};

// Lecture synchrone du cache — pour les besoins ponctuels hors rendu (ex.
// dénormalisation de l'URL d'un favori) : l'URL canonique déjà connue prime.
// Jamais d'URL expirée (purge à la lecture).
export const getCachedStorageUrl = (
  path: string,
  width: MediaWidth = null
): string | undefined => getValidCachedUrl(urlCacheKey(path, width));

// Invalide TOUTES les renditions cachées d'un path (retry-on-expiry des
// téléchargements : une 403/404 du Worker sur une URL expirée purge puis
// re-signe, cf. fetchMediaWithRetry).
export const invalidateMediaUrl = (path: string): void => {
  for (const key of urlCache.keys()) {
    if (pathOfKey(key) === path) urlCache.delete(key);
  }
};

// ============================================================================
// RESOLVER — batching de la callable signMediaUrls
// ============================================================================

interface PendingResolution {
  key: string;
  path: string;
  width: MediaWidth;
  retryCount: number;
  resolvedUrl: boolean;
  resolve: (url: string | null) => void;
}

// File de coalescence : les demandes émises dans une même fenêtre (30 ms —
// imperceptible) partent en UNE requête signMediaUrls par width (contrat
// batch de la callable : une seule requête par écran, rate limit 300/h/uid).
const resolutionQueue = new Map<string, PendingResolution>();
// Contrairement à la file, ce registre survit au départ d'un batch : une
// deuxième demande pour une clé déjà EN VOL partage donc la même promesse au
// lieu d'appeler une seconde fois la callable.
const pendingResolutions = new Map<string, PendingResolution>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let signingRetryNotBefore = 0;
const RESOLUTION_BATCH_DELAY_MS = 30;
const SIGNING_RETRY_BASE_DELAY_MS = 60_000;
const SIGNING_RETRY_MAX_DELAY_MS = 15 * 60_000;

const isSigningRateLimitError = (error: unknown): boolean =>
  ((error as { code?: string } | null)?.code ?? '').endsWith('resource-exhausted');

const rateLimitRetryAfterMs = (error: unknown): number | null => {
  const details = (error as { details?: unknown } | null)?.details;
  const retryAfterMs = (details as { retryAfterMs?: unknown } | undefined)?.retryAfterMs;
  return typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs)
    ? Math.max(0, retryAfterMs)
    : null;
};

const scheduleResolutionFlush = (delayMs = RESOLUTION_BATCH_DELAY_MS): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushResolutionQueue();
  }, Math.max(0, delayMs));
};

// Résolution directe Firebase (path hors périmètre — golden source
// prompts/, templates/…) : comportement inchangé, la golden source reste
// sur Firebase Storage.
const resolveViaFirebase = async (entry: PendingResolution): Promise<void> => {
  try {
    entry.resolve(await getFileUrl(entry.path));
  } catch (err) {
    // Vignette indicative : une résolution en échec ne bloque pas l'écran
    // (la tuile reste affichée sans image).
    console.error('Error resolving storage URL:', err);
    entry.resolve(null);
  }
};

async function flushResolutionQueue(): Promise<void> {
  const retryAfterMs = signingRetryNotBefore - Date.now();
  if (retryAfterMs > 0) {
    scheduleResolutionFlush(retryAfterMs);
    return;
  }

  const batch = Array.from(resolutionQueue.values());
  resolutionQueue.clear();
  if (batch.length === 0) return;

  const signed: PendingResolution[] = [];
  const firebaseResolutions: Promise<void>[] = [];
  for (const entry of batch) {
    if (isR2MediaPath(entry.path)) {
      signed.push(entry);
    }
    // Les paths hors périmètre (golden source) partent en résolution
    // Firebase individuelle, en parallèle, mais participent au même broadcast.
    else {
      firebaseResolutions.push(resolveViaFirebase(entry));
    }
  }

  // Un appel callable par width demandée (width commune à tout le lot),
  // en tranches de 200 (borne MAX_PATHS_PER_CALL de la callable).
  const MAX_PATHS_PER_SIGN_CALL = 200;
  const byWidth = new Map<number, PendingResolution[]>();
  for (const entry of signed) {
    const w = entry.width ?? 0;
    const group = byWidth.get(w) ?? [];
    group.push(entry);
    byWidth.set(w, group);
  }

  await Promise.all([
    ...firebaseResolutions,
    ...Array.from(byWidth.entries()).flatMap(([w, entries]) => {
      const chunks: PendingResolution[][] = [];
      for (let i = 0; i < entries.length; i += MAX_PATHS_PER_SIGN_CALL) {
        chunks.push(entries.slice(i, i + MAX_PATHS_PER_SIGN_CALL));
      }
      return chunks.map(async (chunk) => {
        try {
          const { urls } = await requestSignedMediaUrls(
            chunk.map((e) => e.path),
            w === 0 ? undefined : w
          );
          for (const entry of chunk) {
            // Échec uniforme du videur : un path refusé est absent de la
            // réponse — la tuile reste sans image, jamais de repli Firebase
            // (le fichier n'y existe plus depuis la migration R2).
            entry.resolve(urls[entry.path] ?? null);
          }
        } catch (err) {
          if (isSigningRateLimitError(err)) {
            // Un 429 ne libère PAS les promesses : les rendre nulles ici
            // provoquait une nouvelle tentative à chaque remount/bump, donc
            // une rafale qui maintenait le compteur saturé. On attend le
            // reset annoncé par le serveur (backoff exponentiel en repli).
            const retryCount = Math.max(...chunk.map((entry) => entry.retryCount));
            const backoffMs = Math.min(
              SIGNING_RETRY_MAX_DELAY_MS,
              SIGNING_RETRY_BASE_DELAY_MS * 2 ** Math.min(retryCount, 8)
            );
            const serverRetryAfterMs = rateLimitRetryAfterMs(err);
            const delayMs =
              serverRetryAfterMs === null
                ? backoffMs
                : Math.max(RESOLUTION_BATCH_DELAY_MS, serverRetryAfterMs);
            signingRetryNotBefore = Math.max(
              signingRetryNotBefore,
              Date.now() + delayMs
            );
            for (const entry of chunk) {
              entry.retryCount += 1;
              if (pendingResolutions.get(entry.key) === entry) {
                resolutionQueue.set(entry.key, entry);
              }
            }
            scheduleResolutionFlush(delayMs);
            return;
          }
          console.error('Error signing media URLs:', err);
          chunk.forEach((entry) => entry.resolve(null));
        }
      });
    }),
  ]);

  // Issue #134 : un lot résolu = un seul broadcast, jamais un par URL. Un lot
  // en échec ou réémis après 429 ne re-rend pas les consommateurs (sinon leur
  // effet re-tenterait immédiatement les mêmes clés).
  if (batch.some((entry) => entry.resolvedUrl)) notifyListeners();
}

// Enfile une résolution (dédoublonnée par clé, y compris pendant l'appel
// réseau) et renvoie la promesse de l'URL — le cache est rempli par le flush.
const queueResolution = (
  path: string,
  width: MediaWidth = null
): Promise<string | null> => {
  const key = urlCacheKey(path, width);
  const cached = getValidCachedUrl(key);
  if (cached) return Promise.resolve(cached);
  const pending = pendingResolutions.get(key);
  if (pending) {
    return new Promise((resolve) => {
      const outer = pending.resolve;
      pending.resolve = (url) => {
        outer(url);
        resolve(url);
      };
    });
  }
  pendingKeys.add(key);
  const promise = new Promise<string | null>((resolve) => {
    const entry: PendingResolution = {
      key,
      path,
      width,
      retryCount: 0,
      resolvedUrl: false,
      resolve: (url) => {
        pendingKeys.delete(key);
        pendingResolutions.delete(key);
        if (url && !getValidCachedUrl(key)) {
          setCachedUrl(key, url);
          entry.resolvedUrl = true;
        }
        resolve(url);
      },
    };
    pendingResolutions.set(key, entry);
    resolutionQueue.set(key, entry);
  });
  scheduleResolutionFlush();
  return promise;
};

// Résolution impérative cache-first pour les consommateurs hors rendu
// (téléchargements, zip) : passe par le même cache et la même file batchée.
export const resolveMediaUrl = (
  path: string,
  width: MediaWidth = null
): Promise<string | null> => queueResolution(path, width);

// Fetch d'un média avec RETRY-ON-EXPIRY (correctif post-revue US R2-05) :
// une 403/404 du Worker sur une URL signée expirée déclenche UNE purge du
// cache + re-signature via la callable puis un nouvel essai — jamais de
// téléchargement cassé silencieux. Un vrai 404 (objet absent) est retenté
// une fois pour rien : coût d'une requête, comportement identique au final.
export const fetchMediaWithRetry = async (
  path: string,
  width: MediaWidth = null,
  init?: RequestInit
): Promise<Response> => {
  let url = await resolveMediaUrl(path, width);
  if (!url) throw new Error(`Cannot resolve media URL: ${path}`);
  let response = await fetch(url, init);
  if (
    (response.status === 403 || response.status === 404) &&
    isWorkerSignedUrl(url)
  ) {
    invalidateMediaUrl(path);
    url = await resolveMediaUrl(path, width);
    if (url) response = await fetch(url, init);
  }
  return response;
};

// Extrait le path Storage d'une URL dénormalisée (favoris créés avant ou
// après la bascule) : URL Worker signée (/{width?}/{path}?exp&sig) ou
// token-URL Firebase (/o/{path}?token=). Permet de re-signer des URLs
// dénormalisées expirées (les favoris stockent des URLs, pas des paths).
export const storagePathFromMediaUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.has('sig')) {
      const segments = parsed.pathname.replace(/^\//, '').split('/');
      if (/^\d+$/.test(segments[0])) segments.shift();
      const path = segments.map(decodeURIComponent).join('/');
      return isR2MediaPath(path) ? path : null;
    }
    const match = /\/o\/(.+)$/.exec(parsed.pathname);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

// ============================================================================
// HOOK — vue synchrone du cache + résolution des manquantes
// ============================================================================

// Vignette d'une entrée/run (US R2-05) : le fichier
// thumbnailStoragePath quand il existe (posters vidéo #79, toujours
// produits), sinon la rendition 256 à la volée de l'original — les images
// n'ont plus de vignette fichier depuis la migration R2 (US R2-03).
export const thumbRequestFor = (
  thumbnailStoragePath?: string | null,
  imageStoragePath?: string | null
): StorageUrlRequest =>
  thumbnailStoragePath ??
  (imageStoragePath
    ? { path: imageStoragePath, width: MEDIA_THUMB_WIDTH }
    : undefined);

// Clé de cache correspondant à thumbRequestFor (pour indexer l'objet
// retourné par useStorageUrls).
export const thumbKeyFor = (
  thumbnailStoragePath?: string | null,
  imageStoragePath?: string | null
): string | undefined =>
  thumbnailStoragePath
    ? urlCacheKey(thumbnailStoragePath)
    : imageStoragePath
      ? urlCacheKey(imageStoragePath, MEDIA_THUMB_WIDTH)
      : undefined;

const normalizeRequest = (
  request: StorageUrlRequest
): { path: string; width: MediaWidth } | null => {
  if (!request) return null;
  if (typeof request === 'string') return { path: request, width: null };
  if (!request.path) return null;
  return { path: request.path, width: request.width ?? null };
};

// Préchargement d'URLs en arrière-plan : résout les chemins pas encore en
// cache SANS qu'un composant les demande (aperçus suivants de la galerie).
// La file batchée ne produit qu'un broadcast par flush.
export const prefetchStorageUrls = (requests: StorageUrlRequest[]): void => {
  for (const request of requests) {
    const normalized = normalizeRequest(request);
    if (!normalized) continue;
    const key = urlCacheKey(normalized.path, normalized.width);
    if (getValidCachedUrl(key) || pendingKeys.has(key)) continue;
    void queueResolution(normalized.path, normalized.width);
  }
};

const areRequestsEqual = (
  previous: StorageUrlRequest[],
  next: StorageUrlRequest[]
): boolean =>
  previous.length === next.length &&
  previous.every((request, index) => {
    const other = next[index];
    if (request === other) return true;
    if (!request || !other) return false;
    if (typeof request === 'string' || typeof other === 'string') {
      return request === other;
    }
    return (
      request.path === other.path &&
      (request.width ?? null) === (other.width ?? null)
    );
  });

export const useStorageUrls = (
  requests: StorageUrlRequest[]
): Record<string, string> => {
  // Simple compteur pour re-rendre quand un lot d'URLs arrive en cache.
  const [version, setVersion] = useState(0);

  // Issue #134 : les consommateurs reconstruisent leurs objets requête à
  // chaque render. Comparaison structurelle peu coûteuse et clé conservée —
  // plus de JSON.stringify + tri de toute la galerie à chaque notification.
  const stableRequestsRef = useRef<{
    requests: StorageUrlRequest[];
    key: string;
  } | null>(null);
  if (
    !stableRequestsRef.current ||
    !areRequestsEqual(stableRequestsRef.current.requests, requests)
  ) {
    const key = requests
      .map(normalizeRequest)
      .filter((request): request is { path: string; width: MediaWidth } =>
        Boolean(request)
      )
      .map((request) => urlCacheKey(request.path, request.width))
      .sort()
      .join('\n');
    stableRequestsRef.current = { requests: [...requests], key };
  }
  const requestsKey = stableRequestsRef.current.key;

  // Clé d'identité stable du consommateur pour le registre des chemins
  // demandés (garde anti-éviction de setCachedUrl).
  const consumerKey = useMemo(() => ({}), []);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
      demandedKeys.delete(consumerKey);
    };
  }, [consumerKey]);

  useEffect(() => {
    const uniqueKeys = Array.from(new Set(requestsKey.split('\n').filter(Boolean)));
    // Déclare les chemins affichés par ce consommateur : l'éviction FIFO
    // les saute tant que le composant est monté (anti-clignotement).
    demandedKeys.set(consumerKey, new Set(uniqueKeys));
    const missing = uniqueKeys.filter(
      (key) => !getValidCachedUrl(key) && !pendingKeys.has(key)
    );
    if (missing.length === 0) return;

    missing.forEach((key) => {
      void queueResolution(pathOfKey(key), widthOfKey(key));
    });
    // `version` dans les deps : une entrée évincée du cache borné (issue #82)
    // puis à nouveau demandée est re-résolue au bump suivant.
  }, [requestsKey, version, consumerKey]);

  // Vue synchrone du cache pour les chemins demandés. Une entrée expirée
  // (TTL 7 j des URLs Worker) est purgée puis RE-SIGNÉE en différé (jamais
  // de setState pendant le render — la résolution notifie à l'arrivée).
  return useMemo(() => {
    void version;
    const urls: Record<string, string> = {};
    requestsKey
      .split('\n')
      .filter(Boolean)
      .forEach((key) => {
        const raw = urlCache.get(key);
        if (!raw) return;
        if (isUrlExpired(raw)) {
          urlCache.delete(key);
          setTimeout(() => {
            void queueResolution(pathOfKey(key), widthOfKey(key));
          }, 0);
          return;
        }
        urls[key] = raw;
      });
    return urls;
  }, [requestsKey, version]);
};
